import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-medium-emphasis text-caption" }

import { ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/main';
import language from "@/API/Language/Language";

interface Language {
  id: string;
  code: string;
  name: string;
  order: number;
  rtl: string;
  flag: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'languageView',
  setup(__props) {

const { t } = useI18n();

const active = ref<boolean>(false);
const languages = ref<Language[]>([]);
const selectedLanguage = ref<Language | null>(null);
const languageApi = new language();

async function fetchLanguages() {
  try {
    const response = await languageApi.getAll() as Language[];
    languages.value = response.sort((a: Language, b: Language) => 
      Number(a.order) - Number(b.order)
    );
    
    const savedLangCode = localStorage.getItem('selectedLanguageCode');
    const savedLang = savedLangCode
      ? languages.value.find(lang => lang.code.toLowerCase() === savedLangCode.toLowerCase())
      : undefined;

    if (savedLang) {
      // Restore the previously chosen language exactly as saved.
      (i18n.global.locale as any).value = savedLang.code.toLowerCase();
      selectedLanguage.value = savedLang;
      document.documentElement.dir = savedLang.rtl === 'true' ? 'rtl' : 'ltr';
    } else {
      // No saved preference or the saved code is no longer in the list.
      // Pick the language that matches the locale already loaded by main.ts
      // (which was initialised from localStorage or defaulted to "en").
      // This prevents direction/locale mismatch (e.g. RTL direction with LTR translations).
      const currentLocale = ((i18n.global.locale as any).value || 'en').toLowerCase();
      const matchingLang = languages.value.find(l => l.code.toLowerCase() === currentLocale)
        ?? languages.value[0];

      if (matchingLang) {
        selectedLanguage.value = matchingLang;
        (i18n.global.locale as any).value = matchingLang.code.toLowerCase();
        document.documentElement.dir = matchingLang.rtl === 'true' ? 'rtl' : 'ltr';
        // Persist so that subsequent page loads skip this resolution step.
        localStorage.setItem('selectedLanguageCode', matchingLang.code.toLowerCase());
      }
    }
  } catch (error) {
    console.error('Error fetching languages:', error);
  }
}

function selectLanguage(language: Language) {
  active.value = false;
  
  // Change language
  const newLocale = language.code.toLowerCase();
  (i18n.global.locale as any).value = newLocale;
  selectedLanguage.value = language;
  
  // Save selected language code to localStorage
  localStorage.setItem('selectedLanguageCode', newLocale);
  
  // Set direction based on RTL flag
  document.documentElement.dir = language.rtl === 'true' ? 'rtl' : 'ltr';
  
  // Refresh page to reload all translations
  window.location.reload();
}

onMounted(() => {
  fetchLanguages();
});

// Force position the dropdown to the right when it opens
watch(active, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const dropdownElement = document.querySelector('.language-dropdown-menu .v-overlay__content') as HTMLElement;
      if (dropdownElement) {
        dropdownElement.style.position = 'fixed';
        dropdownElement.style.right = '20px';
        dropdownElement.style.left = 'auto';
        dropdownElement.style.transform = 'none';
        dropdownElement.style.zIndex = '9999';
      }
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    modelValue: active.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((active).value = $event)),
    "close-on-content-click": false,
    location: "bottom start",
    offset: "4",
    attach: false,
    "content-class": "language-dropdown-menu"
  }, {
    activator: _withCtx(({ props }) => [
      _createVNode(_component_v_btn, _mergeProps(props, {
        variant: "outlined",
        class: "text-none",
        style: {"background-color":"#F5F7FA"},
        "prepend-icon": `flag-icon flag-icon-${selectedLanguage.value?.flag}`
      }), {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(selectedLanguage.value?.code) + " ", 1),
          _createVNode(_component_v_icon, { class: "ml-1" }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode("mdi-chevron-down", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      }, 16, ["prepend-icon"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_v_card, { "min-width": "250" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_list, { density: "compact" }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languages.value, (lang) => {
                return (_openBlock(), _createBlock(_component_v_list_item, {
                  key: lang.id,
                  onClick: ($event: any) => (selectLanguage(lang)),
                  class: "cursor-pointer"
                }, {
                  prepend: _withCtx(() => [
                    _createElementVNode("i", {
                      class: _normalizeClass(`flag-icon flag-icon-${lang.flag} m-rtl`)
                    }, null, 2)
                  ]),
                  append: _withCtx(() => [
                    _createElementVNode("span", _hoisted_1, _toDisplayString(lang.code), 1)
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(lang.name), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1032, ["onClick"]))
              }), 128))
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})